<script setup lang="ts">
import { computed } from 'vue';
import { computedAsync } from '@vueuse/core';

import { CustomersRatingTag } from '@apps/main/new-desktop/modules/customersRating';
import { HotelAmenities } from '@apps/main/new-desktop/modules/hotelAmenities';
import {
  useRouterStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { usePreviouslyUserSessionStore } from '@apps/main/new-desktop/core/stores/previouslyUserSession/previouslyUserSessionStore';
import { getBoardBasisText } from '@apps/main/new-desktop/core/helpers/translation.helper';

import { AppStars, AppRouterLink, AppTag, AppSvgIcon } from '@libs/ui';
import { BoardBasisCode } from '@libs/trip-planner-interfaces';
import { isExperimentAsync } from '@libs/feature-toggle';

import type {
  HotelSelectionCardContentData,
  HotelSelectionCardContext,
} from '../models';

interface HotelSelectionCardContentProps {
  data: HotelSelectionCardContentData;
  context: HotelSelectionCardContext;
}

const props = defineProps<HotelSelectionCardContentProps>();

const routerStore = useRouterStore();
const statsigEventsStore = useStatsigEventsStore();
const previouslyUserSessionStore = usePreviouslyUserSessionStore();

const isRemoveBulletPointsHotelCardExperiment = computedAsync(
  () => isExperimentAsync('remove_bullet_points_hotel_card'),
  false
);

function onNavigationTriggered(): void {
  const isPreviouslySeenHotel =
    previouslyUserSessionStore.previouslySeenHotelCodes
      .includes(props.context.hotelCode)
      .toString();
  statsigEventsStore.logHotelSelectionListCardClicked({
    isPreviouslySeenHotel,
  });
}

const boardBasisLabel = computed<string | undefined>(() => {
  if (
    props.data.boardBasis === undefined ||
    props.data.boardBasis[0] === undefined ||
    props.data.boardBasis[0] === BoardBasisCode.RO
  ) {
    return undefined;
  }

  return getBoardBasisText(props.data.boardBasis[0]);
});

const shouldDisplayCancellationPolicies = computed<boolean>(
  () => props.data.freeCancellation
);
</script>

<template>
  <div class="hotel-selection-card-content">
    <div class="hotel-details-section">
      <div>
        <AppRouterLink
          :link="
            routerStore.getHotelDetailsUrlInHotelSelection(
              context.ingredientIndex,
              context.hotelCode
            )
          "
          font="font-body-m-bold"
          color="color-neutral-70"
          class="name"
          @navigation-triggered="onNavigationTriggered"
        >
          {{ props.data.title }}
        </AppRouterLink>
        <div class="location">{{ props.data.subtitle }}</div>
        <div class="ratings">
          <AppStars class="stars" :amount="props.data.stars" size="18" />
          <CustomersRatingTag
            v-if="props.data.guestRating"
            :guest-rating="props.data.guestRating"
          />
        </div>
        <HotelAmenities class="amenities" :amenities="props.data.amenities" />
        <ul
          class="nearby-places"
          :class="{
            'remove-bullet-points-hotel-card-experiment':
              isRemoveBulletPointsHotelCardExperiment,
          }"
        >
          <li v-for="content in props.data.content" :key="content" class="item">
            <span class="item-text">
              {{ content }}
            </span>
          </li>
        </ul>
      </div>
    </div>
    <AppTag
      v-if="shouldDisplayCancellationPolicies"
      class="cancellation-policies"
      type="positive"
      :icon="{ name: 'infoCircle', size: '18', position: 'right' }"
      size="xs"
    >
      <template #title>
        <span>{{ $t('filter.rules.free_cancellation') }}</span>
      </template>
    </AppTag>
    <div
      v-if="boardBasisLabel"
      class="meal-info-section"
      :class="{ wide: !shouldDisplayCancellationPolicies }"
    >
      <AppSvgIcon name="food" size="18" color="color-neutral-70" />
      <div class="meal-info">
        <span>{{ boardBasisLabel }}</span>
        <span v-if="props.data.areOtherBoardBasisOptions">
          <span>.&nbsp;</span>
          <span class="extra-info">
            {{ $t('planner.other_meal_plans_available') }}
          </span>
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.hotel-selection-card-content {
  width: 100%;
  height: 100%;

  display: grid;
  grid-template-columns: 75% 25%;
  grid-template-rows: 1fr min-content;

  .hotel-details-section {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: var(--spacing-2);
    height: 100%;
    grid-column: 1/-1;
    grid-row: 1/2;
    .name {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .location {
      @include font-body-s-light;
      text-transform: capitalize;
      color: var(--color-neutral-70);
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .amenities {
      margin: var(--spacing-4) 0;
    }
    .ratings {
      margin: var(--spacing-4) 0 var(--spacing-8) 0;
      display: flex;
      flex-wrap: wrap;
      gap: var(--spacing-12);
      margin-bottom: var(--spacing-8);
    }

    ul {
      margin: 0 0 0 var(--spacing-4);
      padding-top: var(--spacing-8);
      padding-left: var(--spacing-16);
      list-style: none;

      .item {
        @include font-label-s-light;
        color: var(--color-neutral-70);
        position: relative;

        // NOTE: Standard list-style is broken on mobile,
        // when using overflow + ellipsis on list element.
        // Hence custom dot element
        &::before {
          content: '';
          width: 3px;
          height: 3px;
          border-radius: 50%;
          position: absolute;
          background: var(--color-neutral-70);
          left: -9px;
          top: 50%;
          transform: translateY(-50%);
        }

        .item-text {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          line-clamp: 1;
          -webkit-box-orient: vertical;
          word-break: break-all;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        &:not(:last-child) {
          margin-bottom: var(--spacing-4);
        }
      }
    }
  }

  .cancellation-policies {
    grid-column: 1/2;
    grid-row: 2/3;
    @include font-label-s-bold;
    padding: var(--spacing-2) var(--spacing-8);
  }

  .meal-info-section {
    display: flex;
    align-self: center;
    align-items: center;
    gap: var(--spacing-4);

    grid-column: 1/2;
    grid-row: 2/-1;
    &.wide {
      grid-column: 1/-1;
    }

    .meal-info {
      @include font-body-s-bold;
      .extra-info {
        @include font-body-s-light;
      }
    }
  }
}

@container hotelCard (max-width: 690px) {
  .hotel-selection-card-content {
    .hotel-details-section {
      grid-column: 1/-1;
    }
  }
  .remove-bullet-points-hotel-card-experiment {
    display: none;
  }
}
</style>
